// src/pages/admin/Funcoes.js
import React, { useEffect, useMemo, useState } from "react";
import { useTable, useFilters, useSortBy } from "react-table";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "../components/PageHeader";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
// import MoreVertIcon from "@mui/icons-material/MoreVert";
// import Menu from "@mui/material/Menu";
// import MenuItem from "@mui/material/MenuItem";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

// ------------------- Filtros -------------------
function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar..."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
      onFocus={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.35)")}
      onBlur={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.2)")}
    />
  );
}

// ------------------- Persistência de filtros -------------------
const FILTERS_KEY = "tbl:funcoes:filters";
const KEEP_KEY = "tbl:funcoes:keep";

export default function Funcoes() {
  const { showAlert } = useAlert();
  const { temPermissao } = useAuth();

  const [data, setData] = useState([]);

  // “Manter filtros” – por defeito TRUE
  const initialKeep =
    (() => {
      try {
        const v = localStorage.getItem(KEEP_KEY);
        return v === null ? true : JSON.parse(v);
      } catch {
        return true;
      }
    })() ?? true;
  const [keepFilters, setKeepFilters] = useState(initialKeep);

  const initialFilters = keepFilters
    ? (() => {
        try {
          return JSON.parse(localStorage.getItem(FILTERS_KEY) || "[]");
        } catch {
          return [];
        }
      })()
    : [];

  // ------------------- Load -------------------
  const carregar = async () => {
    const [payload, err] = await handleApi(api.post("Funcoes/SelFuncoes", {}));
    if (err) return showAlert(err, "error");
    setData(Array.isArray(payload) ? payload : []);
  };

  useEffect(() => {
    (async () => {
      await carregar();
    })();
  }, []);

  // ------------------- Inserir / Editar / Eliminar -------------------
  const [openAdd, setOpenAdd] = useState(false);
  const [novo, setNovo] = useState({ designacao: "", ausencias: 0 });

  const [openEdit, setOpenEdit] = useState(false);
  const [sel, setSel] = useState(null);

  // Dialog de confirmação de eliminação
  const [openConfirm, setOpenConfirm] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const inserir = async () => {
    if (!novo.designacao?.trim()) return showAlert("Indica a designação.", "warning");
    const [_, err, meta] = await handleApi(
      api.post("Funcoes/InsFuncao", {
        designacao: novo.designacao.trim(),
        ausencias: novo.ausencias,
      })
    );

    if (err) return showAlert(err, "error");
    if (meta?.message) showAlert(meta.message, meta.severity || "success");
    await carregar();
    if (!keepFilters) setAllFilters([]);
    setOpenAdd(false);
    setNovo({ designacao: "", ausencias: 0 });
  };

  const gravarEdicao = async () => {
    if (!sel?.designacao?.trim()) return showAlert("Indica a designação.", "warning");
    const [_, err, meta] = await handleApi(
      api.post("Funcoes/UpdFuncao", {
        id: sel.id,
        designacao: sel.designacao.trim(),
        ausencias: sel.ausencias ?? 0,
      })
    );

    if (err) return showAlert(err, "error");
    if (meta?.message) showAlert(meta.message, meta.severity || "success");
    await carregar();
    if (!keepFilters) setAllFilters([]);
    setOpenEdit(false);
  };

  const pedirConfirmacao = (row) => {
    setToDelete(row?.original || null);
    setOpenConfirm(true);
  };
  const cancelarEliminacao = () => {
    setOpenConfirm(false);
    setToDelete(null);
  };
  const confirmarEliminacao = async () => {
    if (!toDelete?.id) return cancelarEliminacao();
    const [_, err, meta] = await handleApi(api.post("Funcoes/DelFuncao", { id: toDelete.id }));
    if (err) showAlert(err, "error");
    else if (meta?.message) showAlert(meta.message, meta.severity || "success");
    await carregar();
    if (!keepFilters) setAllFilters([]);
    cancelarEliminacao();
  };

  // ------------------- Tabela -------------------
  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        width: 72,
        minWidth: 56,
        maxWidth: 90,
        Cell: ({ row }) => {
          const a = row.original;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <IconButton
                color="warning"
                size="small"
                disabled={!temPermissao("Menu Administração", "u")}
                onClick={() => {
                  setSel({
                    ...a,
                    ausencias: a.ausencias ?? 0,
                  });
                  setOpenEdit(true);
                }}
                title="Editar"
              >
                <EditIcon fontSize="small" />
              </IconButton>

              <IconButton
                color="error"
                size="small"
                disabled={!temPermissao("Menu Administração", "d")}
                onClick={() => pedirConfirmacao(row)}
                title="Eliminar"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          );
        },
      },
      { Header: "Designação", accessor: "designacao", Filter: TextFilter },
      {
        Header: "Ausências",
        accessor: (row) => row.ausencias?.toString?.() ?? "0",
        id: "ausencias",
        Filter: ({ column: { filterValue, setFilter } }) => (
          <select
            value={filterValue || ""}
            onChange={(e) => setFilter(e.target.value || undefined)}
            style={{
              width: "100%",
              padding: "6px 4px",
              border: "none",
              borderBottom: "1px solid rgba(0,0,0,0.2)",
              outline: "none",
              background: "transparent",
              fontSize: 12,
              color: "#556",
            }}
          >
            <option value="">Todas</option>
            <option value="1">Sim</option>
            <option value="0">Não</option>
          </select>
        ),
        Cell: ({ row }) => <span>{row.original.ausencias === 1 ? "Sim" : "Não"}</span>,
      },
    ],
    [temPermissao, keepFilters]
  );

  const table = useTable(
    {
      columns,
      data,
      initialState: { filters: initialFilters },
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setAllFilters, state } =
    table;

  // Regras de persistência dos filtros
  useEffect(() => {
    if (!keepFilters && (state.filters?.length || 0) > 0) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    try {
      localStorage.setItem(KEEP_KEY, JSON.stringify(keepFilters));
    } catch {}
    if (!keepFilters) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
  }, [keepFilters, setAllFilters]);

  useEffect(() => {
    if (!keepFilters) return;
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify(state.filters || []));
    } catch {}
  }, [state.filters, keepFilters]);

  // ------------------- UI -------------------
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader />

      <MDBox pt={1}>
        <Card>
          <MDBox p={3}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              {/* <MDTypography variant="h6">Funções</MDTypography> */}

              <MDBox display="flex" alignItems="center" gap={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={keepFilters}
                      onChange={(e) => setKeepFilters(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Manter filtros"
                />

                <MDButton
                  color="success"
                  disabled={!temPermissao("Menu Administração", "w")}
                  onClick={() => setOpenAdd(true)}
                >
                  Nova Função
                </MDButton>
              </MDBox>
            </MDBox>

            <MDBox sx={{ height: "68vh", overflow: "auto", borderRadius: 1 }}>
              <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  {headerGroups.map((hg) => (
                    <tr {...hg.getHeaderGroupProps()}>
                      {hg.headers.map((col) => (
                        <th
                          {...col.getHeaderProps(col.getSortByToggleProps())}
                          style={{
                            width: col.id === "acoes" ? 72 : undefined,
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            background: "#fff",
                            textAlign: "left",
                            padding: "6px 8px",
                            fontSize: 12,
                            color: "#7b809a",
                            verticalAlign: "bottom",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center" }}>
                            {col.render("Header")}
                            {col.isSorted ? (
                              col.isSortedDesc ? (
                                <span style={{ marginLeft: 6 }}>▼</span>
                              ) : (
                                <span style={{ marginLeft: 6 }}>▲</span>
                              )
                            ) : null}
                          </div>
                          {!col.disableFilters && (
                            <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                              {col.canFilter ? col.render("Filter") : null}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            style={{
                              width: cell.column.id === "acoes" ? 72 : undefined,
                              whiteSpace: "nowrap",
                              padding: "8px 10px",
                              borderTop: "1px solid #eee",
                              fontSize: 12,
                              color: "#1a2b4c",
                            }}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>

      {/* EDITAR */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Função</DialogTitle>
        <DialogContent>
          <MDBox display="grid" gap={1.25} mt={0.5}>
            <MDInput
              label="Designação"
              value={sel?.designacao || ""}
              onChange={(e) =>
                setSel((p) => ({
                  ...p,
                  designacao: e.target.value,
                }))
              }
              fullWidth
            />
          </MDBox>

          <FormControlLabel
            control={
              <Checkbox
                checked={sel?.ausencias === 1}
                onChange={(e) =>
                  setSel((old) => ({
                    ...old,
                    ausencias: e.target.checked ? 1 : 0,
                  }))
                }
                size="small"
              />
            }
            label="Função usada em Ausências"
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenEdit(false)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={gravarEdicao} color="success">
            Guardar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ADICIONAR */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova Função</DialogTitle>
        <DialogContent>
          <MDBox display="grid" gap={1.25} mt={0.5}>
            <MDInput
              label="Designação"
              value={novo.designacao}
              onChange={(e) =>
                setNovo((p) => ({
                  ...p,
                  designacao: e.target.value,
                }))
              }
              fullWidth
            />
          </MDBox>

          <FormControlLabel
            control={
              <Checkbox
                checked={novo.ausencias === 1}
                onChange={(e) =>
                  setNovo((old) => ({
                    ...old,
                    ausencias: e.target.checked ? 1 : 0,
                  }))
                }
                size="small"
              />
            }
            label="Função usada em Ausências"
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenAdd(false)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={inserir} color="success">
            Inserir
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* CONFIRMAR ELIMINAÇÃO */}
      <Dialog open={openConfirm} onClose={cancelarEliminacao} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar eliminação</DialogTitle>
        <DialogContent>
          Tem a certeza que pretende eliminar a função <strong>{toDelete?.designacao ?? ""}</strong>
          ?
        </DialogContent>
        <DialogActions>
          <MDButton onClick={cancelarEliminacao} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={confirmarEliminacao} color="error">
            Eliminar
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
